import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { X, Upload, Download, FileText, Warning, CircleNotch, CheckCircle } from '@phosphor-icons/react';
import payrollService from '../../services/payrollService';
import '../../styles/ContractBulkImportModal.css';

function ContractBulkImportModal({ isOpen, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState([]);
  const fileInputRef = useRef(null);

  const EXPECTED_COLUMNS = [
    'staff_id',
    'basic_salary',
    'pay_schedule',
    'allowance_housing',
    'allowance_transport',
    'allowance_meals',
    'allowance_utilities',
    'allowance_other',
    'allowance_other_description',
    'tax_rate_percent',
    'pension_rate_percent',
    'nssf_rate_percent',
    'other_deduction',
    'other_deduction_description',
    'effective_from',
    'notes'
  ];

  const downloadTemplate = () => {
    const headers = EXPECTED_COLUMNS.join(',');
    const exampleRow = [
      'STF001',
      '5000.00',
      'monthly',
      '500',
      '200',
      '150',
      '0',
      '0',
      '',
      '15',
      '5',
      '5',
      '0',
      '',
      new Date().toISOString().split('T')[0],
      'Initial contract'
    ].join(',');

    const csvContent = `${headers}\n${exampleRow}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'payroll_contracts_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV template downloaded!');
  };

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      setFile(null);
      return;
    }

    setFile(selectedFile);

    // Preview first few rows
    try {
      const text = await selectedFile.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      // Validate headers
      const missingColumns = EXPECTED_COLUMNS.filter(col => !headers.includes(col));
      if (missingColumns.length > 0) {
        toast.error(`Missing columns: ${missingColumns.join(', ')}`);
        setFile(null);
        setPreview([]);
        return;
      }

      // Parse first 5 rows for preview
      const previewRows = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',');
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx]?.trim() || '';
          });
          previewRows.push(row);
        }
      }
      setPreview(previewRows);
      toast.success(`File ready! Found ${previewRows.length} contracts to import`);
    } catch (error) {
      toast.error('Error reading file');
      setFile(null);
      setPreview([]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        toast.error('Please drop a CSV file');
        return;
      }
      fileInputRef.current.files = e.dataTransfer.files;
      handleFileSelect({ target: { files: e.dataTransfer.files } });
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await payrollService.importContracts(formData);

      setResults(response);
      
      if (response.success) {
        toast.success(`Imported ${response.success_count} contracts successfully!`);
        setTimeout(() => {
          onSuccess?.();
          handleClose();
        }, 1500);
      } else if (response.success_count > 0) {
        toast.warning(`Imported ${response.success_count} contracts with ${response.errors?.length || 0} errors`);
      } else {
        toast.error(`${response.message}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Import failed');
      setResults({
        success: false,
        message: error.message,
        errors: [],
        success_count: 0
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      <div className="modal-content bulk-import-modal">
        <div className="modal-header">
          <h2>Bulk Import Payroll Contracts</h2>
          <button className="close-btn" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        <div className="bulk-import-content">
          {!results ? (
            <>
              <div className="import-info">
                <Warning size={20} />
                <p>Import multiple payroll contracts at once from a CSV file</p>
              </div>

              {/* Template Download */}
              <div className="template-section">
                <button className="btn btn-secondary template-btn" onClick={downloadTemplate}>
                  <Download size={16} /> Download CSV Template
                </button>
                <p className="template-hint">Download the template to see the required format</p>
              </div>

              {/* File Upload Area */}
              <div
                className="upload-area"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".csv"
                  style={{ display: 'none' }}
                />
                <button
                  className="upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={32} />
                  <span>Drag and drop CSV file here or click to browse</span>
                </button>
              </div>

              {/* File Info */}
              {file && (
                <div className="file-info">
                  <FileText size={16} />
                  <div>
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      setFile(null);
                      setPreview([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Preview Table */}
              {preview.length > 0 && (
                <div className="preview-section">
                  <h3>Preview ({preview.length} records)</h3>
                  <div className="preview-table-container">
                    <table className="preview-table">
                      <thead>
                        <tr>
                          <th>Staff ID</th>
                          <th>Basic Salary</th>
                          <th>Pay Schedule</th>
                          <th>Allowances</th>
                          <th>Deductions</th>
                          <th>Effective From</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((row, idx) => (
                          <tr key={idx}>
                            <td className="mono">{row.staff_id}</td>
                            <td className="text-right">GHS {parseFloat(row.basic_salary || 0).toFixed(2)}</td>
                            <td className="capitalize">{row.pay_schedule}</td>
                            <td className="text-right">
                              GHS {(
                                parseFloat(row.allowance_housing || 0) +
                                parseFloat(row.allowance_transport || 0) +
                                parseFloat(row.allowance_meals || 0) +
                                parseFloat(row.allowance_utilities || 0) +
                                parseFloat(row.allowance_other || 0)
                              ).toFixed(2)}
                            </td>
                            <td>
                              {row.tax_rate_percent}% tax, {row.pension_rate_percent}% pension
                            </td>
                            <td>{row.effective_from}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImport}
                  disabled={!file || uploading}
                >
                  {uploading ? (
                    <>
                      <CircleNotch size={16} weight="fill" className="spinner" /> Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} /> Import Contracts
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Results */}
              <div className={`results-section ${results.success ? 'success' : 'partial'}`}>
                <div className="results-header">
                  <h3>
                    {results.success ? (
                      <>
                        <CheckCircle size={20} weight="fill" /> Import Successful
                      </>
                    ) : (
                      <>
                        <Warning size={20} weight="fill" /> Import Completed
                      </>
                    )}
                  </h3>
                  <p className="results-message">{results.message}</p>
                </div>

                {/* Summary Stats */}
                <div className="results-stats">
                  <div className="stat-card success">
                    <p className="stat-label">Imported</p>
                    <p className="stat-value">{results.success_count}</p>
                  </div>
                  {results.errors?.length > 0 && (
                    <div className="stat-card error">
                      <p className="stat-label">Errors</p>
                      <p className="stat-value">{results.errors.length}</p>
                    </div>
                  )}
                </div>

                {/* Error Details */}
                {results.errors?.length > 0 && (
                  <div className="errors-section">
                    <h4>Errors ({results.errors.length})</h4>
                    <div className="errors-list">
                      {results.errors.slice(0, 10).map((error, idx) => (
                        <div key={idx} className="error-item">
                          <Warning size={16} />
                          <span>{error}</span>
                        </div>
                      ))}
                      {results.errors.length > 10 && (
                        <p className="more-errors">
                          ... and {results.errors.length - 10} more errors
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Close Action */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ContractBulkImportModal;
